<template>
  <div class="docutax-client-profile" v-loading="isLoading">
    <el-page-header @back="goBack" :content="fullName" class="mb-3" />

    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card class="box-card" :body-style="{ padding: '20px 20px 2px 20px' }">
          <div slot="header" class="clearfix detail-header">
            <i class="fa fa-user"></i> Client Details
            <el-button type="primary" size="small" icon="fa fa-pencil-alt" @click="onEditClient">Edit</el-button>
            <el-button type="primary" size="small" icon="fa el-icon-s-promotion" @click="onSendRequest">Send New
              Request</el-button>
          </div>
          <el-row :gutter="18" justify="start">
            <el-col :sm="12" :lg="6">
              <static-field label="Name" :value="fullName" />
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Phone" :value="profile.client ? profile.client.cellPhone : ''" />
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Email" :value="profile.client ? profile.client.emailAddress : ''" />
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Verified" :value="profile.client && profile.client.verified ? 'Yes' : 'No'" />
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Signup Source" :value="profile.client ? profile.client.signUpSource : ''" />
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Office" :value="profile.client ? profile.client.selectedOfficeUserId : ''" />
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Created" :value="formatDate(profile.client ? profile.client.createdDate : null)" />
            </el-col>
            <el-col :sm="12" :lg="6">
              <static-field label="Signup Completed"
                :value="formatDate(profile.client ? profile.client.signupCompletionDate : null)" />
            </el-col>
          </el-row>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card>
          <div slot="header" class="clearfix detail-header">
            <span><i class="fa fa-file-invoice" /> Submissions ({{ profile.submissions.length }})</span>
          </div>
          <docutax-submissions-table :submissions="profile.submissions" :show-client-column="false"
            @sendRequest="onSendRequest" @rowUpdated="onSubmissionRowUpdated" />
          <div v-if="profile.submissions.length === 0" class="empty-state">
            No submissions found for this client.
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-row :gutter="18">
      <el-col :md="24" class="mb-3">
        <el-card>
          <div slot="header" class="clearfix detail-header">
            <span><i class="fa fa-paper-plane" /> Requests ({{ profile.requests.length }})</span>
          </div>
          <docutax-requests-table :requests="profile.requests" :show-client-column="false"
            @sendRequest="onSendRequest" />
          <div v-if="profile.requests.length === 0" class="empty-state">
            No requests found for this client.
          </div>
        </el-card>
      </el-col>
    </el-row>

    <el-dialog title="New Docutax Request" :visible.sync="requestDialogVm.visible">
      <docutax-request-for-client-dialog v-if="profile.client" :docutax-client="profile.client"
        @requestSent="onRequestSent" @close="onRequestDialogClose" />
    </el-dialog>

    <el-dialog title="Edit Client" :visible.sync="editDialogVm.visible">
      <edit-docutax-client-dialog v-if="profile.client" :client="profile.client" @clientUpdated="onClientUpdated"
        @close="onEditDialogClose" />
    </el-dialog>
  </div>
</template>

<script>
import moment from 'moment';
import get from 'lodash/get';
import api from '@/api';
import StaticField from '@/components/widgets/StaticField.vue';
import DocutaxSubmissionsTable from '@/components/tables/DocutaxSubmissions.vue';
import DocutaxRequestsTable from '@/components/tables/DocutaxRequests.vue';
import DocutaxRequestForClientDialog from '@/components/dialog-content/DocutaxRequestForClient.vue';
import EditDocutaxClientDialog from '@/components/dialog-content/EditDocutaxClient.vue';
import DocutaxClientProfile from '@/model/DocutaxClientProfile';
import DocutaxSubmissionSearchResult from '@/model/DocutaxSubmissionSearchResult';

export default {
  name: 'DocutaxClientProfileView',
  components: {
    StaticField,
    DocutaxSubmissionsTable,
    DocutaxRequestsTable,
    DocutaxRequestForClientDialog,
    EditDocutaxClientDialog,
  },
  data() {
    return {
      isLoading: false,
      profile: new DocutaxClientProfile(),
      requestDialogVm: {
        visible: false,
      },
      editDialogVm: {
        visible: false,
      },
    };
  },
  watch: {
    '$route.params.clientId': {
      immediate: true,
      handler(clientId) {
        if (!clientId) return;
        this.loadProfile(clientId);
      },
    },
  },
  computed: {
    fullName() {
      if (!this.profile.client) return '';
      return [this.profile.client.firstName, this.profile.client.lastName]
        .filter(part => !!part && part.length)
        .join(' ');
    },
  },
  methods: {
    async loadProfile(clientId) {
      try {
        this.isLoading = true;
        const response = await api.docutaxClient.getProfile(clientId);
        this.profile = new DocutaxClientProfile(response.data);
      } catch (e) {
        const errorMessage = get(e, 'response.data.message', get(e, 'message', 'An error occurred while loading the client profile'));

        this.$notify.error({
          title: 'Failed to load client',
          message: errorMessage,
          position: 'bottom-right',
        });
      } finally {
        this.isLoading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      if (date.date) {
        return moment(date.date).format('L LT');
      }
      return moment(date).format('L LT');
    },
    goBack() {
      const isInternal = document.referrer && new URL(document.referrer).origin === window.location.origin;

      if (isInternal) {
        // Go back one step in history
        this.$router.go(-1);
      } else {
        // Redirect to the fallback path within your app
        this.$router.push({ name: 'docutaxSubmissionList' });
      }
    },
    onSendRequest() {
      this.requestDialogVm.visible = true;
    },
    onRequestSent() {
      this.$message.success('Request sent successfully');
    },
    onRequestDialogClose() {
      this.requestDialogVm.visible = false;
      // Reload profile to get updated requests
      this.loadProfile(this.$route.params.clientId);
    },
    onSubmissionRowUpdated(updatedRow) {
      const index = this.profile.submissions.findIndex(s => s.id === updatedRow.id);
      if (index !== -1) {
        this.$set(this.profile.submissions, index, new DocutaxSubmissionSearchResult(updatedRow));
      }
    },
    onEditClient() {
      this.editDialogVm.visible = true;
    },
    onClientUpdated(updatedClient) {
      this.profile.client = updatedClient;
    },
    onEditDialogClose() {
      this.editDialogVm.visible = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.detail-header {
  font-size: 18px;
  font-weight: bold;
  line-height: 24px;

  i {
    margin-right: 8px;
  }

  button {
    float: right;
    padding: 5px 5px;
    margin-left: 5px;
  }
}

.empty-state {
  color: #909399;
  text-align: center;
  padding: 20px;
  font-style: italic;
}
</style>
